import { useCallback, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';

import { MdAccountFilterInput, MdAccountSortByFieldEnum } from '@anyx/common-graphql';
import { BasicOption } from '@anyx/shared-utils';


import { ACCOUNTS_SELECT } from '../graphql';

const SELECT_ALL_PAGE_SIZE = 100;

/**
 * Upper bound on a single "select all". Narrowing by country keeps real usage far
 * below this; the cap only stops an unfiltered click from pulling every account in
 * the workspace into one user's access list.
 */
export const SELECT_ALL_LIMIT = 500;

const SORT_BY = {
  descending: false,
  field: MdAccountSortByFieldEnum.COMPANY_NAME,
};

export const useAccountsSelectAll = <T = string>({
  filters,
  skip,
}: {
  filters?: MdAccountFilterInput;
  skip?: boolean;
}) => {
  const [loadingAll, setLoadingAll] = useState(false);
  const [queryAccounts] = useLazyQuery(ACCOUNTS_SELECT);

  // Cheapest way to label the action with a count before anyone clicks it.
  const { data } = useQuery(ACCOUNTS_SELECT, {
    skip,
    variables: {
      pageNumber: 1,
      pageSize: 1,
      filter: { ...filters },
      sortBy: SORT_BY,
    },
  });

  const total = data?.MdAccounts.total ?? 0;

  const loadAllOptions = useCallback(async (): Promise<BasicOption<T>[]> => {
    setLoadingAll(true);

    try {
      const options: BasicOption<T>[] = [];
      let pageNumber = 1;

      for (;;) {
        const { data: page } = await queryAccounts({
          variables: {
            pageNumber,
            pageSize: SELECT_ALL_PAGE_SIZE,
            filter: { ...filters },
            sortBy: SORT_BY,
          },
        });

        const items = page?.MdAccounts.items ?? [];
        // Guards against a server that reports a total it cannot page through.
        if (items.length === 0) {
          break;
        }

        options.push(...items.map(({ id, name }) => ({ label: name, value: id as unknown as T })));

        if (options.length >= Math.min(page?.MdAccounts.total ?? 0, SELECT_ALL_LIMIT)) {
          break;
        }

        pageNumber += 1;
      }

      return options.slice(0, SELECT_ALL_LIMIT);
    } finally {
      setLoadingAll(false);
    }
  }, [filters, queryAccounts]);

  return { total, loadAllOptions, loadingAll };
};
